import { Injectable, inject } from '@angular/core';
import { AITool } from '../tool.decorator';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { CallPlanOfflineService } from '@omni/services/call-plan/call-plan.offline.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { EventName, EventsService } from '@omni/services/events/events.service';
import { ComponentViewMode, UIService } from '@omni/services/ui/ui.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';

/** Values of the contact page tabs that `view_contact` can open. */
const CONTACT_TABS = ['feed', 'info', 'profile', 'customer-assess', 'consent', 'capture', 'timeline', 'journey'];

/**
 * Tools that primarily drive navigation around the CRM. They share a single
 * dependency footprint (navigation + UI + a couple of domain services) and
 * mostly fan out to `events.publish('navigateTo', ...)` plus a handful of
 * page-pushing flows for "view <entity>" cases.
 */
@Injectable({ providedIn: 'root' })
export class NavigationTools {
  private readonly accountService = inject(AccountOfflineService);
  private readonly contactService = inject(ContactOfflineService);
  private readonly callPlanService = inject(CallPlanOfflineService);
  private readonly navService = inject(NavigationService);
  private readonly uiService = inject(UIService);
  private readonly events = inject(EventsService);
  private readonly diskService = inject(DiskService);

  private openMenuOption(menuOptionId: string, successMessage: string) {
    const navigation = { opened: false };
    this.events.publish('navigateTo', menuOptionId, navigation);
    return navigation.opened
      ? { success: true, message: successMessage }
      : { success: false, error: `${menuOptionId} is not in this user's menu` };
  }

  @AITool('navigate_to_feature', 'Navigate to different sections of the CRM application', [
    {
      name: 'feature_id',
      type: 'string',
      description: 'The feature/section to navigate to. Available options: accounts, contacts, presentations, resources, call-plan, accountManagement, quotes, orderManagement, case-tool, coaching, survey, time-off, allocation, scientificactivity, opportunitymgmt, marketscan, scheduler, eventstool, pharmacovigilance, fieldMaterialManagement, marketingBusiness, dashboard, calendar',
      required: true,
    },
  ])
  async navigateToFeature(feature_id: string): Promise<any> {
    try {
      return this.openMenuOption(feature_id, `Navigation to ${feature_id} initiated`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Navigation failed' };
    }
  }

  @AITool('navigate_to_accounts', 'Navigate to the accounts page')
  async navigateToAccounts(): Promise<any> {
    try {
      return this.openMenuOption('accounts', 'Navigated to Accounts page');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to navigate to accounts' };
    }
  }

  @AITool('navigate_to_contacts', 'Navigate to the contacts page')
  async navigateToContacts(): Promise<any> {
    try {
      return this.openMenuOption('contacts', 'Navigated to Contacts page');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to navigate to contacts' };
    }
  }

  @AITool('navigate_to_call_plans', 'Navigate to the call plans page in the CRM to view all call plans')
  async navigateToCallPlans(): Promise<any> {
    try {
      this.callPlanService.selectedCallPlan = null;
      return this.openMenuOption('call-plan', 'Navigated to Call Plans page');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to navigate to call plans' };
    }
  }

  @AITool('navigate_to_presentations', 'Navigate to the presentations page in the CRM')
  async navigateToPresentations(): Promise<any> {
    try {
      return this.openMenuOption('presentations', 'Navigated to Presentations page');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to navigate to presentations' };
    }
  }

  @AITool('view_account', 'Navigate to and view a specific account by ID', [
    { name: 'account_id', type: 'string', description: 'The ID of the account to view', required: true },
  ])
  async viewAccount(account_id: string): Promise<any> {
    try {
      const account = this.accountService.getAccountById(account_id);

      if (!account) {
        return {
          success: false,
          message: `Account not found with ID: ${account_id}, you can use the search_accounts tool to find accounts by name or other criteria`,
        };
      }

      this.accountService.isClickedGoToAccountsTool = true;
      this.accountService.selected = account;
      await this.navService.pushWithPageTracking(
        (await import('@omni/components/account/account-page/account-page')).AccountPageComponent,
        PageName.AccountPageComponent,
        {
          from: 'AccountTool',
          __source: 'menu',
          accountInformation: account,
        },
        PageName.AccountPageComponent,
      );

      return {
        success: true,
        message: `Viewing account: ${account.accountName}`,
        account: {
          id: account.id,
          name: account.accountName,
          type: account.accountLevel,
          city: account.city,
        },
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to view account' };
    }
  }

  @AITool('view_contact', 'Navigate to and view a specific contact by ID', [
    { name: 'contact_id', type: 'string', description: 'The ID of the contact to view', required: true },
    {
      name: 'tab',
      type: 'string',
      description: `Optional contact page tab to open: ${CONTACT_TABS.join(', ')}. Omit it to open the default tab.`,
      required: false,
      enum: CONTACT_TABS,
    },
  ])
  async viewContact(contact_id: string, tab?: string): Promise<any> {
    try {
      const contact = this.contactService.getContactByID(contact_id);

      if (!contact) {
        return {
          success: false,
          message: `Contact not found with ID: ${contact_id}, you can use the search_contacts tool to find contacts by name or other criteria`,
        };
      }

      this.contactService.isClickedGoToContactsTool = true;
      this.contactService.contactInformation = contact;
      await this.navService.pushWithPageTracking(
        (await import('@omni/components/contact/contact-page/contact-page')).ContactPageComponent,
        PageName.ContactPageComponent,
        {
          from: 'ContactTool',
          __source: 'menu',
          contactInformation: contact,
          tab,
        },
        PageName.ContactPageComponent,
      );
      const segment = CONTACT_TABS.find(name => name === tab?.trim().toLowerCase());
      if (segment) {
        this.uiService.contactDetailsSegment = segment;
        this.events.publish(EventName.CONTACTDETAILSSEGMENTUPDATE);
      }

      return {
        success: true,
        message: `Viewing contact: ${contact.fullName}`,
        contact: {
          id: contact.ID,
          name: contact.fullName,
          title: contact.jobTitle,
          account: contact.accountName,
        },
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to view contact' };
    }
  }

  @AITool(
    'view_call_plan',
    "Select and navigate to view a specific call plan's details page in the CRM. Use this when the user wants to open or view a specific call plan.",
    [
      {
        name: 'call_plan_id',
        type: 'string',
        description: 'The customer ID, contact ID, account ID, or customer name for the call plan to view',
        required: true,
      },
    ],
  )
  async viewCallPlan(call_plan_id: string): Promise<any> {
    try {
      const callPlansDoc = await this.diskService.retrieve(DB_KEY_PREFIXES.MY_POSITON_CALL_PLANS);

      if (!callPlansDoc || !callPlansDoc.raw || callPlansDoc.raw.length === 0) {
        return { success: false, message: 'No call plans data available' };
      }

      const allPlans = this.callPlanService.aggregateRepCallPlans(callPlansDoc.raw);

      const callPlan = allPlans.find(plan =>
        plan.customerId === call_plan_id ||
        plan.contactId === call_plan_id ||
        plan.accountId === call_plan_id ||
        plan.cycleplanid === call_plan_id ||
        plan.customerName?.toLowerCase().includes(call_plan_id.toLowerCase()),
      );

      if (!callPlan) {
        return { success: false, message: `Call plan not found for: ${call_plan_id}` };
      }

      if (callPlan.customerType === 'contact' && callPlan.contactId) {
        const contact = this.contactService.getContactByID(callPlan.contactId);
        if (contact) {
          this.contactService.contactInformation = contact;
        }
        this.accountService.selected = undefined;
      } else if (callPlan.customerType === 'account' && callPlan.accountId) {
        const accounts = this.accountService.accounts || [];
        const account = accounts.find(a => a.id === callPlan.accountId);
        if (account) {
          this.accountService.selected = account;
        }
        this.contactService.contactInformation = undefined;
      }

      this.callPlanService.selectedCallPlan = callPlan;

      this.uiService.prevView = this.uiService.activeView;
      this.uiService.activeView = 'CallPlans';

      await this.navService.setRootWithTracking(
        (await import('@omni/components/customer-call-plans/customer-call-plan-page/customer-call-plan-page'))
          .CustomerCallPlanPage,
        PageName.CustomerCallPlanPage,
        {
          listMode: ComponentViewMode.LISTVIEW,
          __source: 'menu',
          customerType: callPlan.customerType,
        },
        PageName.CustomerCallPlanPage,
      );

      return {
        success: true,
        message: `Opened call plan for ${callPlan.customerName}`,
        callPlan: {
          customerName: callPlan.customerName,
          customerType: callPlan.customerType,
          completionPercentage: callPlan.percentage || 0,
        },
      };
    } catch (error) {
      console.error('[NavigationTools] Error viewing call plan:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to view call plan' };
    }
  }
}
